import { useEffect } from "react";
import useQueryInsights from "admin/hooks/useQueryInsights";
import {
  QUERY_INSIGHTS_APP_IDS,
  QUERY_INSIGHTS_PAGE_TITLE,
} from "admin/types/constants/queryInsights";
import type { ClassificationSummary } from "admin/types/interfaces/queryInsights";
import { Layers, Loader2, MessageCircleQuestion, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { StatsCard } from "~/components/ui/stats-card";
import useAppHeader from "~/contexts/header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { trackEvent } from "~/utils/monitoring";
import { DistributionBarChart, VolumeLineChart } from "./charts";

const QueryInsights = ({ isNextGen = false }: { isNextGen?: boolean }) => {
  useDocumentTitle(QUERY_INSIGHTS_PAGE_TITLE);
  useAppHeader({ title: QUERY_INSIGHTS_PAGE_TITLE });

  useEffect(() => {
    trackEvent("admin_query_insights_viewed");
  }, []);

  const { summary, isLoading, error } = useQueryInsights({
    appIds: [...QUERY_INSIGHTS_APP_IDS],
  });
  const hasSummary = !!summary && !error;

  return (
    <AdminPage
      title={QUERY_INSIGHTS_PAGE_TITLE}
      description="What kind of queries are users asking AI agents over the last six months"
      constrainWidth={isNextGen}
    >
      {isLoading ? (
        <SummaryLoading />
      ) : !hasSummary ? (
        <SummaryUnavailable />
      ) : (
        <>
          <HeadlineCards summary={summary} />
          {summary.total === 0 ? <EmptyWindow /> : <ChartsGrid summary={summary} />}
        </>
      )}
    </AdminPage>
  );
};

const HeadlineCards = ({ summary }: { summary: ClassificationSummary }) => {
  const topCategory = summary.byCategory[0] ? humanizeLabel(summary.byCategory[0].category) : "—";

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <StatsCard
        title={summary.total.toLocaleString()}
        description="Classified queries (6 mo)"
        icon={MessageCircleQuestion}
        className="border"
      />
      <StatsCard
        title={summary.byCategory.length}
        description="Distinct categories"
        icon={Layers}
        className="border"
      />
      <StatsCard
        title={topCategory}
        description="Top category"
        icon={Sparkles}
        className="border"
      />
    </div>
  );
};

const ChartsGrid = ({ summary }: { summary: ClassificationSummary }) => (
  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <ChartCard title="Queries by Category">
      <DistributionBarChart
        data={summary.byCategory.map((c) => ({
          label: humanizeLabel(c.category),
          count: c.count,
        }))}
      />
    </ChartCard>
    <ChartCard title="Queries by Subcategory">
      <DistributionBarChart data={summary.bySubcategory.slice(0, 10)} />
    </ChartCard>
    <ChartCard title="Query Complexity">
      <DistributionBarChart
        data={summary.byComplexity.map((c) => ({
          label: humanizeLabel(c.label),
          count: c.count,
        }))}
      />
    </ChartCard>
    <ChartCard title="Classifier Confidence">
      <DistributionBarChart
        data={summary.byConfidence.map((b) => ({
          label: `${b.lower.toFixed(1)}–${b.upper.toFixed(1)}`,
          count: b.count,
        }))}
      />
    </ChartCard>
    <div className="lg:col-span-2">
      <ChartCard title="Volume Over Time">
        <VolumeLineChart data={summary.volumeByDay} />
      </ChartCard>
    </div>
  </div>
);

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card className="shadow-none">
    <CardHeader className="pb-2">
      <CardTitle className="text-base font-semibold">{title}</CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const SummaryLoading = () => (
  <div className="flex items-center justify-center py-12">
    <div className="flex items-center gap-3 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span>Loading insights…</span>
    </div>
  </div>
);

const SummaryUnavailable = () => (
  <StatusMessage
    title="Insights are not available right now"
    body="Something went wrong. Try again in a moment."
  />
);

const EmptyWindow = () => (
  <StatusMessage
    title="No classified queries in the last six months"
    body="Insights will appear here as users interact with the assistants."
    icon={<MessageCircleQuestion className="size-8" aria-hidden="true" />}
  />
);

const humanizeLabel = (value: string): string =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");

export default QueryInsights;
